import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Check, ChevronDown, Loader2, RefreshCw, ShieldCheck, AlertTriangle, Wallet } from 'lucide-react';
import { toast } from 'sonner';

function formatApiError(err, fallback = 'Failed to save settlement details') {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const detail = data?.detail ?? data?.message ?? data?.error ?? data;
  if (detail == null && err?.message === 'Network Error') {
    return 'Unable to reach the server. Please check your connection and try again.';
  }
  if (status >= 500 && !detail) {
    return 'The server could not complete this request right now. Please try again in a moment.';
  }
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item?.message || item?.detail || JSON.stringify(item)).filter(Boolean).join(' ');
  }
  if (detail?.message) return detail.message;
  if (detail?.error) return typeof detail.error === 'string' ? detail.error : JSON.stringify(detail.error);
  if (typeof err?.message === 'string' && err.message) return err.message;
  return fallback;
}

function statusClass(status) {
  const value = String(status || '').toLowerCase();
  if (['active', 'approved', 'subaccount_created'].includes(value)) return 'bg-emerald-100 text-emerald-800';
  if (['pending', 'pending_admin_approval'].includes(value)) return 'bg-amber-100 text-amber-800';
  if (['failed', 'rejected', 'disabled'].includes(value)) return 'bg-red-100 text-red-800';
  return 'bg-slate-100 text-slate-700';
}

function StatusPill({ value }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusClass(value)}`}>
      {String(value || 'pending').replace(/_/g, ' ')}
    </span>
  );
}

function SearchablePicker({
  label,
  value,
  options,
  onChange,
  placeholder = 'Select',
  searchPlaceholder = 'Search...',
  emptyText = 'No matches found.',
  disabled = false,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = options.find((item) => item.value === value);
  const filtered = useMemo(() => {
    const term = String(query || '').toLowerCase().trim();
    if (!term) return options;
    return options.filter((item) => {
      const haystack = `${item.label || ''} ${item.value || ''} ${item.meta || ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [options, query]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <div className="space-y-1.5">
      {label ? <Label>{label}</Label> : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between border-slate-200 bg-white"
            disabled={disabled}
            data-testid={testId}
          >
            <span className="truncate text-left">{selected?.label || placeholder}</span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} onValueChange={setQuery} />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <CommandGroup>
                {filtered.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.value}
                    onSelect={() => {
                      onChange(item);
                      setOpen(false);
                    }}
                    className="flex items-center justify-between"
                  >
                    <span className="truncate">{item.label}</span>
                    {value === item.value ? <Check className="h-4 w-4 text-emerald-600" /> : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function FieldError({ error }) {
  if (!error) return null;
  return <p className="text-xs text-red-600 mt-1">{error}</p>;
}

export default function Settlement() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [subaccountData, setSubaccountData] = useState(null);
  const [subaccountError, setSubaccountError] = useState('');
  const [overview, setOverview] = useState(null);
  const [withdrawalSummary, setWithdrawalSummary] = useState(null);

  const [form, setForm] = useState({
    subaccount_name: '',
    subaccount_email: '',
    country: '',
    country_code: '',
    currency: '',
    account_type: 'bank_account',
    bank_name: '',
    bank_code: '',
    bank_branch: '',
    branch_code: '',
    account_number: '',
    account_name: '',
    swift_code: '',
    split_type: 'percentage',
    jjuma_share_percentage: 5,
    subaccount_share_percentage: 95,
    split_value: 0,
    flutterwave_split_enabled: false,
    auto_create_subaccount_enabled: false,
    manual_subaccount_id_allowed: false,
    settlement_status: 'pending',
    settlement_phone_number: '',
    settlement_mobile_network: '',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [profileRes, banksRes, overviewRes, withdrawalsRes] = await Promise.all([
        api.get('/dashboard/settlement'),
        api.get('/dashboard/settlement/banks', { params: { active_only: true } }),
        api.get('/dashboard/overview'),
        api.get('/withdrawals', { params: { page: 1, limit: 1 } }),
      ]);
      const profileData = profileRes.data?.profile || {};
      const loadedBanks = banksRes.data?.banks || [];
      const resolvedCountry = profileData.country || profileData.settlement_country || '';
      const resolvedCountryCode = loadedBanks.find((bank) => bank.country === resolvedCountry)?.country_code || profileData.country_code || '';
      const resolvedCurrency = profileData.currency || profileData.default_currency || loadedBanks.find((bank) => bank.country === resolvedCountry)?.currency || 'UGX';
      setProfile(profileData);
      setOverview(overviewRes?.data || null);
      setWithdrawalSummary(withdrawalsRes?.data?.summary || null);
      setSubaccountData(profileRes.data?.subaccount || null);
      setSubaccountError(profileRes.data?.subaccount_error || profileData.flutterwave_subaccount_error || '');
      setBanks(loadedBanks);
      setForm((prev) => ({
        ...prev,
        subaccount_name: profileData.subaccount_name || profileData.business_name || profileData.name || '',
        subaccount_email: profileData.subaccount_email || profileData.email || '',
        country: resolvedCountry,
        country_code: resolvedCountryCode,
        currency: resolvedCurrency,
        account_type: profileData.settlement_account_type || profileData.account_type || 'bank_account',
        bank_name: profileData.bank_name || profileData.settlement_bank_name || '',
        bank_code: profileData.bank_code || profileData.settlement_bank_code || '',
        bank_branch: profileData.bank_branch || profileData.settlement_bank_branch || '',
        branch_code: profileData.branch_code || '',
        account_number: profileData.account_number || profileData.settlement_account_number || '',
        account_name: profileData.account_name || profileData.settlement_account_name || '',
        swift_code: profileData.swift_code || '',
        split_type: profileData.split_type || 'percentage',
        jjuma_share_percentage: Number(profileData.jjuma_share_percentage ?? 5),
        subaccount_share_percentage: Number(profileData.subaccount_share_percentage ?? 95),
        split_value: Number(profileData.split_value ?? 0),
        flutterwave_split_enabled: !!profileData.flutterwave_split_enabled,
        auto_create_subaccount_enabled: !!profileData.auto_create_subaccount_enabled,
        manual_subaccount_id_allowed: !!profileData.manual_subaccount_id_allowed,
        settlement_status: profileData.settlement_status || 'pending',
        settlement_phone_number: profileData.settlement_phone_number || profileData.phone || '',
        settlement_mobile_network: profileData.settlement_mobile_network || '',
      }));
    } catch (err) {
      setSubaccountError(formatApiError(err, 'Failed to load settlement details'));
      toast.error(formatApiError(err, 'Failed to load settlement details'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const countryOptions = useMemo(() => {
    const map = new Map();
    banks.forEach((bank) => {
      if (bank.country) map.set(bank.country, { value: bank.country, label: bank.country, meta: bank.country_code || '' });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [banks]);

  const currencyOptions = useMemo(() => {
    const filtered = banks.filter((bank) => !form.country || bank.country === form.country);
    const map = new Map();
    filtered.forEach((bank) => {
      if (bank.currency) map.set(bank.currency, { value: bank.currency, label: bank.currency });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [banks, form.country]);

  const visibleBanks = useMemo(() => {
    return banks.filter((bank) => {
      if (form.country && bank.country !== form.country) return false;
      if (form.country_code && /^[A-Z]{2,3}$/.test(form.country_code.toUpperCase()) && bank.country_code && bank.country_code !== form.country_code.toUpperCase()) return false;
      if (form.currency && bank.currency && bank.currency !== form.currency) return false;
      if (form.account_type && bank.account_type && bank.account_type !== form.account_type) return false;
      return true;
    });
  }, [banks, form.country, form.country_code, form.currency, form.account_type]);

  const selectedBank = useMemo(() => {
    return visibleBanks.find((bank) => bank.bank_code === form.bank_code || bank.bank_name === form.bank_name) || null;
  }, [visibleBanks, form.bank_code, form.bank_name]);

  const branchOptions = useMemo(() => {
    const branchList = Array.isArray(selectedBank?.branches) ? selectedBank.branches : [];
    return branchList.map((branch) => ({
      value: branch.branch_code || branch.branch_name,
      label: branch.branch_name,
      meta: branch.branch_code || '',
    }));
  }, [selectedBank]);

  const requiresSwift = !!selectedBank?.requires_swift_code && form.account_type === 'bank_account';
  const branchRequired = form.account_type === 'mobile_money' || branchOptions.length > 0;

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handleCountryChange = (item) => {
    const nextCountry = item?.value || '';
    const nextCountryCode = String(item?.meta || '').toUpperCase();
    const nextCurrencies = banks.filter((bank) => bank.country === nextCountry).map((bank) => bank.currency).filter(Boolean);
    const nextCurrency = nextCurrencies.includes(form.currency) ? form.currency : (nextCurrencies[0] || form.currency || 'UGX');
    setForm((prev) => ({
      ...prev,
      country: nextCountry,
      country_code: nextCountryCode || '',
      currency: nextCurrency,
      bank_name: '',
      bank_code: '',
      bank_branch: '',
      branch_code: '',
    }));
  };

  const handleCurrencyChange = (item) => {
    setForm((prev) => ({
      ...prev,
      currency: item?.value || '',
      bank_name: '',
      bank_code: '',
      bank_branch: '',
      branch_code: '',
    }));
  };

  const handleAccountTypeChange = (value) => {
    const nextBanks = banks.filter((bank) => {
      if (form.country && bank.country !== form.country) return false;
      if (form.country_code && bank.country_code && bank.country_code !== form.country_code.toUpperCase()) return false;
      if (form.currency && bank.currency && bank.currency !== form.currency) return false;
      return bank.account_type === value;
    });
    const firstBank = nextBanks[0] || null;
    setForm((prev) => ({
      ...prev,
      account_type: value,
      bank_name: value === 'mobile_money' ? (firstBank?.bank_name || 'Mobile money') : '',
      bank_code: firstBank?.bank_code || '',
      bank_branch: firstBank?.branches?.[0]?.branch_name || '',
      branch_code: firstBank?.branches?.[0]?.branch_code || '',
      swift_code: value === 'mobile_money' ? '' : prev.swift_code,
      account_number: '',
    }));
  };

  const handleBankChange = (item) => {
    const bank = visibleBanks.find((row) => row.bank_code === item?.value);
    setForm((prev) => ({
      ...prev,
      bank_name: bank?.bank_name || item?.label || '',
      bank_code: bank?.bank_code || item?.value || '',
      bank_branch: bank?.branches?.[0]?.branch_name || '',
      branch_code: bank?.branches?.[0]?.branch_code || '',
      swift_code: bank?.requires_swift_code ? prev.swift_code : '',
      account_type: bank?.account_type || prev.account_type,
    }));
  };

  const handleBranchChange = (item) => {
    const branch = branchOptions.find((row) => row.value === item?.value);
    setForm((prev) => ({
      ...prev,
      bank_branch: branch?.label || item?.label || '',
      branch_code: branch?.meta || item?.meta || item?.value || '',
    }));
  };

  const handleJjumaShareChange = (value) => {
    const jjumaShare = Number(value || 0);
    const subShare = Math.max(0, 100 - jjumaShare);
    setForm((prev) => ({
      ...prev,
      jjuma_share_percentage: jjumaShare,
      subaccount_share_percentage: subShare,
      split_value: prev.split_type === 'percentage' ? Number((subShare / 100).toFixed(6)) : prev.split_value,
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.subaccount_name) nextErrors.subaccount_name = 'Subaccount name is required.';
    if (!form.subaccount_email) nextErrors.subaccount_email = 'Subaccount email is required.';
    if (!form.country) nextErrors.country = 'Country is required.';
    if (!form.currency) nextErrors.currency = 'Currency is required.';
    if (!form.account_type) nextErrors.account_type = 'Account type is required.';
    if (!form.bank_name) nextErrors.bank_name = 'Bank name is required.';
    if (branchRequired && !form.bank_branch) nextErrors.bank_branch = 'Bank branch is required.';
    if (!form.account_number) nextErrors.account_number = form.account_type === 'mobile_money' ? 'Mobile money phone number is required.' : 'Account number is required.';
    if (!form.account_name) nextErrors.account_name = 'Account name is required.';
    if (requiresSwift && !form.swift_code) nextErrors.swift_code = 'Swift code is required for this bank.';
    if (form.split_type === 'percentage') {
      const total = Number(form.jjuma_share_percentage || 0) + Number(form.subaccount_share_percentage || 0);
      if (Math.round(total * 100) / 100 !== 100) {
        nextErrors.split_type = 'JJuma share and merchant share must total 100%.';
      }
    } else if (Number(form.split_value || 0) <= 0) {
      nextErrors.split_value = 'Flat split value must be greater than zero.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => ({
    ...form,
    country_code: form.country_code || form.country || '',
    currency: form.currency || 'UGX',
    settlement_account_type: form.account_type || 'bank_account',
    split_type: form.split_type || 'percentage',
    jjuma_share_percentage: Number(form.jjuma_share_percentage || 0),
    subaccount_share_percentage: Number(form.subaccount_share_percentage || 0),
    split_value: Number(form.split_value || 0),
    flutterwave_split_enabled: !!form.flutterwave_split_enabled,
    auto_create_subaccount_enabled: !!form.auto_create_subaccount_enabled,
    manual_subaccount_id_allowed: !!form.manual_subaccount_id_allowed,
    bank_code: form.bank_code || '',
    branch_code: form.branch_code || '',
  });

  const persist = async (submit = false) => {
    if (!validate()) {
      toast.error('Please fix the highlighted fields.');
      return;
    }
    const payload = buildPayload();
    setSaving(!submit);
    setSubmitting(submit);
    try {
      const endpoint = submit ? '/dashboard/settlement/submit' : '/dashboard/settlement';
      const { data } = await api[submit ? 'post' : 'put'](endpoint, payload);
      const nextProfile = data?.profile || {};
      setProfile(nextProfile);
      setSubaccountData(data?.subaccount || subaccountData);
      setSubaccountError(nextProfile.flutterwave_subaccount_error || data?.subaccount_error || '');
      setForm((prev) => ({
        ...prev,
        settlement_status: nextProfile.settlement_status || (submit ? 'pending' : prev.settlement_status),
        flutterwave_split_enabled: !!nextProfile.flutterwave_split_enabled,
        auto_create_subaccount_enabled: !!nextProfile.auto_create_subaccount_enabled,
        manual_subaccount_id_allowed: !!nextProfile.manual_subaccount_id_allowed,
        bank_name: nextProfile.bank_name || prev.bank_name,
        bank_code: nextProfile.bank_code || prev.bank_code,
        bank_branch: nextProfile.bank_branch || prev.bank_branch,
        branch_code: nextProfile.branch_code || prev.branch_code,
        account_number: nextProfile.account_number || prev.account_number,
        account_name: nextProfile.account_name || prev.account_name,
        swift_code: nextProfile.swift_code || prev.swift_code,
        jjuma_share_percentage: Number(nextProfile.jjuma_share_percentage ?? prev.jjuma_share_percentage ?? 0),
        subaccount_share_percentage: Number(nextProfile.subaccount_share_percentage ?? prev.subaccount_share_percentage ?? 0),
        split_value: Number(nextProfile.split_value ?? prev.split_value ?? 0),
      }));
      toast.success(data?.message || (submit ? 'Settlement submitted' : 'Settlement details saved'));
      await loadData();
    } catch (err) {
      const message = formatApiError(err, submit ? 'Failed to submit settlement details' : 'Failed to save settlement details');
      setSubaccountError(message);
      toast.error(message);
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  const canViewSwift = requiresSwift && form.account_type === 'bank_account';
  const settlementMode = String(profile?.settlement_mode || (form.flutterwave_split_enabled ? 'split' : 'wallet') || '').toLowerCase();
  const settlementModeLabel = settlementMode === 'split'
    ? 'Split Payment'
    : settlementMode === 'wallet'
      ? 'JJuma Wallet'
      : settlementMode === 'manual'
        ? 'Manual Review'
        : 'Not set';
  const wallet = overview?.wallets?.[0] || {};
  const currency = wallet?.currency || profile?.currency || 'UGX';
  const splitSettledAmount = Number(withdrawalSummary?.split_settled_amount || 0);
  const availableBalance = Number(withdrawalSummary?.available_for_withdrawal ?? wallet?.available_balance ?? 0);
  const pendingBalance = Number(withdrawalSummary?.pending_balance || 0);
  const completedWithdrawals = Number(withdrawalSummary?.completed_withdrawals || 0);
  const failedWithdrawals = Number(withdrawalSummary?.failed_withdrawals || 0);
  const paymentStatus = profile?.flutterwave_subaccount_status || 'missing';

  if (loading) {
    return <div className="flex items-center justify-center h-56"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Settlement</h1>
        <p className="text-slate-500 text-sm mt-0.5">Submit Flutterwave split settlement details for admin approval.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Settlement Mode</p>
            <p className="text-sm font-semibold text-slate-900 mt-1">{settlementModeLabel}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Flutterwave Split Status</p>
            <div className="mt-2"><StatusPill value={form.settlement_status || profile?.settlement_status} /></div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Flutterwave Subaccount ID</p>
            <p className="text-sm font-medium text-slate-900 mt-1 break-all">{profile?.flutterwave_subaccount_id || 'Not created yet'}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Subaccount Status</p>
            <div className="mt-2"><StatusPill value={paymentStatus} /></div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Wallet Balance</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-1">{`${currency} ${Number(wallet.total_balance || 0).toLocaleString()}`}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Available for Withdrawal</p>
            <p className={`text-2xl font-bold mono-nums mt-1 ${availableBalance > 0 ? 'text-emerald-700' : 'text-slate-900'}`}>{`${currency} ${availableBalance.toLocaleString()}`}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Paid Directly Through Flutterwave Split</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-1">{`${currency} ${splitSettledAmount.toLocaleString()}`}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Pending Settlement / Withdrawals</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-1">{`${currency} ${pendingBalance.toLocaleString()}`}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-3">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Completed Withdrawals</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-1">{completedWithdrawals}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Failed Withdrawals</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-1">{failedWithdrawals}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-slate-900">Withdrawal details live in Bank Details</p>
            <p className="text-xs text-slate-500 mt-1">Settlement is for Flutterwave split approval and bank details are kept separate to avoid confusion.</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard/bank-details')} className="border-slate-200 text-slate-700">
            Open Bank Details
          </Button>
        </CardContent>
      </Card>

      {profile?.account_status !== 'approved' && profile?.verification_status !== 'verified' && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle className="mt-0.5 text-amber-600" size={18} />
          <div>
            <p className="text-sm font-semibold text-amber-900">Approval required</p>
            <p className="text-xs text-amber-700 mt-1">You can save settlement details now, but admin approval is required before automatic subaccount creation.</p>
          </div>
        </div>
      )}

      <Card className="rounded-2xl border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Settlement Details</CardTitle>
          <p className="text-xs text-slate-500">Use the same account details shown in Flutterwave’s subaccount form.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 flex items-start gap-2">
            <ShieldCheck size={14} className="mt-0.5 text-emerald-600" />
            <div>
              <p className="font-medium text-slate-700">Withdrawal details are managed separately</p>
              <p className="mt-1">
                Use Settlement for split payout setup. Use Bank Details for withdrawal bank or mobile money details.
                {' '}
                <button type="button" className="text-emerald-700 font-medium hover:underline" onClick={() => navigate('/dashboard/bank-details')}>
                  Go to Bank Details
                </button>
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Subaccount Name</Label>
              <Input value={form.subaccount_name} onChange={(e) => updateField('subaccount_name', e.target.value)} className="mt-1" placeholder="Business or merchant name" data-testid="settlement-subaccount-name" />
              <FieldError error={errors.subaccount_name} />
            </div>
            <div>
              <Label>Subaccount Email</Label>
              <Input type="email" value={form.subaccount_email} onChange={(e) => updateField('subaccount_email', e.target.value)} className="mt-1" placeholder="merchant@example.com" data-testid="settlement-subaccount-email" />
              <FieldError error={errors.subaccount_email} />
            </div>
            <SearchablePicker
              label="Country"
              value={form.country}
              options={countryOptions}
              onChange={handleCountryChange}
              placeholder="Select country"
              testId="settlement-country"
            />
            <SearchablePicker
              label="Currency"
              value={form.currency}
              options={currencyOptions.length ? currencyOptions : [{ value: 'UGX', label: 'UGX' }, { value: 'KES', label: 'KES' }]}
              onChange={handleCurrencyChange}
              placeholder="Select currency"
              testId="settlement-currency"
            />
            <div>
              <Label>Account Type</Label>
              <Select value={form.account_type} onValueChange={handleAccountTypeChange}>
                <SelectTrigger className="mt-1" data-testid="settlement-account-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_account">Bank Account</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                </SelectContent>
              </Select>
              <FieldError error={errors.account_type} />
            </div>
            <SearchablePicker
              label="Bank Name"
              value={form.bank_code || form.bank_name}
              options={visibleBanks.map((bank) => ({
                value: bank.bank_code || bank.bank_name,
                label: bank.bank_name,
                meta: `${bank.country || ''} ${bank.currency || ''} ${bank.account_type || ''}`,
              }))}
              onChange={handleBankChange}
              placeholder={form.account_type === 'mobile_money' ? 'Select mobile money network' : 'Select bank'}
              searchPlaceholder={form.account_type === 'mobile_money' ? 'Search network...' : 'Search bank...'}
              emptyText="No matching bank or mobile money option."
              testId="settlement-bank-name"
            />
            <div>
              <Label>{form.account_type === 'mobile_money' ? 'Branch / Network' : 'Bank Branch'}</Label>
              {branchOptions.length > 0 ? (
                <SearchablePicker
                  value={form.branch_code || form.bank_branch}
                  options={branchOptions}
                  onChange={handleBranchChange}
                  placeholder="Select branch"
                  searchPlaceholder="Search branch..."
                  emptyText="No branch found."
                  testId="settlement-bank-branch"
                />
              ) : (
                <Input value={form.bank_branch} onChange={(e) => updateField('bank_branch', e.target.value)} className="mt-1" placeholder="Enter bank branch" data-testid="settlement-bank-branch" />
              )}
              <FieldError error={errors.bank_branch} />
            </div>
            <div>
              <Label>{form.account_type === 'mobile_money' ? 'Mobile Money Phone Number' : 'Account Number'}</Label>
              <Input
                value={form.account_number}
                onChange={(e) => updateField('account_number', e.target.value)}
                className="mt-1"
                placeholder={form.account_type === 'mobile_money' ? '2567XXXXXXXX' : 'Account number'}
                data-testid="settlement-account-number"
              />
              <FieldError error={errors.account_number} />
            </div>
            <div>
              <Label>Account Name</Label>
              <Input value={form.account_name} onChange={(e) => updateField('account_name', e.target.value)} className="mt-1" placeholder="Account holder name" data-testid="settlement-account-name" />
              <FieldError error={errors.account_name} />
            </div>
            {canViewSwift ? (
              <div>
                <Label>Swift Code</Label>
                <Input value={form.swift_code} onChange={(e) => updateField('swift_code', e.target.value)} className="mt-1" placeholder="Swift code" data-testid="settlement-swift-code" />
                <FieldError error={errors.swift_code} />
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-500 flex items-start gap-2">
                <ShieldCheck size={14} className="mt-0.5 text-emerald-600" />
                <div>
                  <p className="font-medium text-slate-700">Swift code hidden</p>
                  <p className="mt-1">This account type does not require a Swift code right now.</p>
                </div>
              </div>
            )}
            <div>
              <Label>Split Type</Label>
              <Select value={form.split_type} onValueChange={(value) => updateField('split_type', value)}>
                <SelectTrigger className="mt-1" data-testid="settlement-split-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="flat">Flat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.split_type === 'percentage' ? (
              <>
                <div>
                  <Label>JJuma Share Percentage</Label>
                  <Input type="number" min="0" max="100" value={form.jjuma_share_percentage} onChange={(e) => handleJjumaShareChange(e.target.value)} className="mt-1" data-testid="settlement-jjuma-share" />
                  <FieldError error={errors.split_type} />
                </div>
                <div>
                  <Label>Merchant Share Percentage</Label>
                  <Input type="number" min="0" max="100" value={form.subaccount_share_percentage} readOnly className="mt-1 bg-slate-50" data-testid="settlement-merchant-share" />
                </div>
              </>
            ) : (
              <div>
                <Label>Split Value</Label>
                <Input type="number" min="0" value={form.split_value} onChange={(e) => updateField('split_value', e.target.value)} className="mt-1" data-testid="settlement-split-value" />
                <FieldError error={errors.split_value} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <input type="checkbox" checked={form.flutterwave_split_enabled} onChange={(e) => updateField('flutterwave_split_enabled', e.target.checked)} className="w-4 h-4" data-testid="settlement-split-enabled" />
              <span className="text-sm text-slate-700">Enable Flutterwave split payments</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <input type="checkbox" checked={form.auto_create_subaccount_enabled} onChange={(e) => updateField('auto_create_subaccount_enabled', e.target.checked)} className="w-4 h-4" data-testid="settlement-auto-create" />
              <span className="text-sm text-slate-700">Auto create Flutterwave subaccount</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <input type="checkbox" checked={form.manual_subaccount_id_allowed} onChange={(e) => updateField('manual_subaccount_id_allowed', e.target.checked)} className="w-4 h-4" data-testid="settlement-manual-id" />
              <span className="text-sm text-slate-700">Allow manual subaccount ID entry by admin only</span>
            </label>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Wallet size={16} className="text-emerald-600" />
              <p className="text-sm font-semibold text-slate-900">Approval & Status</p>
            </div>
            <p className="text-xs text-slate-500">Status is updated automatically when admin approves your details or the Flutterwave subaccount is created.</p>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <StatusPill value={form.settlement_status || profile?.settlement_status} />
              <StatusPill value={profile?.flutterwave_subaccount_status} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <Label>Flutterwave Response Log</Label>
              <textarea
                readOnly
                value={JSON.stringify(subaccountData || profile?.flutterwave_subaccount_response || {}, null, 2)}
                className="mt-1 w-full min-h-[170px] rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-mono text-slate-700"
                data-testid="settlement-response-log"
              />
            </div>
            <div>
              <Label>Flutterwave Error Log</Label>
              <textarea
                readOnly
                value={subaccountError || profile?.flutterwave_subaccount_error || ''}
                className="mt-1 w-full min-h-[170px] rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-mono text-slate-700"
                data-testid="settlement-error-log"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={() => persist(false)}
              disabled={saving || submitting}
              data-testid="settlement-save-btn"
            >
              {saving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : null}
              Save Draft
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              onClick={() => persist(true)}
              disabled={saving || submitting}
              data-testid="settlement-submit-btn"
            >
              {submitting ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : null}
              Submit for Admin Approval
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="text-slate-600"
              onClick={loadData}
              disabled={saving || submitting}
              data-testid="settlement-refresh-btn"
            >
              <RefreshCw size={16} className="mr-1.5" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
